import { createFileRoute, Link } from "@tanstack/react-router";
import { Toaster } from "@/components/ui/sonner";
import { BookingPanel } from "@/components/BookingPanel";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StickyMobileBar } from "@/components/StickyMobileBar";
import { MobileHeroCta } from "@/components/MobileHeroCta";
import { IdleCallNudge } from "@/components/IdleCallNudge";
import { TrustRow } from "@/components/TrustRow";
import { trackConversion } from "@/lib/gtag";
import heroSedan from "@/assets/hero-sedan.webp";
import vehicleSedan from "@/assets/vehicle-sedan.jpg";
import vehicleSuv from "@/assets/vehicle-suv.jpg";
import vehicleMaxi from "@/assets/vehicle-maxi.jpg";
import {
  Phone,
  Plane,
  Clock,
  Luggage,
  Users,
  ShieldCheck,
  Star,
  MapPin,
  Car,
  HelpCircle,
} from "lucide-react";

const URL = "https://sepremiumcabs.com/melbourne-airport-taxi";

/** Typical road distance/duration from Melbourne Airport (Tullamarine). */
const ROUTES: { to: string; km: number; min: number }[] = [
  { to: "Melbourne CBD", km: 23, min: 30 },
  { to: "Box Hill", km: 32, min: 35 },
  { to: "Glen Waverley", km: 40, min: 40 },
  { to: "Dandenong", km: 52, min: 48 },
  { to: "Berwick", km: 62, min: 55 },
  { to: "Frankston", km: 65, min: 60 },
  { to: "Cranbourne", km: 65, min: 58 },
  { to: "Pakenham", km: 75, min: 65 },
];

const AIRPORT_FAQS: { q: string; a: string }[] = [
  {
    q: "How much is a taxi from Melbourne Airport to the CBD?",
    a: "Melbourne Airport to the CBD is about 23 km. Our fares follow the official Victorian taxi rates, so you always pay the correct metered price with no surge. Call 0493 100 265 or use the booking form to get your quote before you travel.",
  },
  {
    q: "What happens if my flight is delayed?",
    a: "We track your flight using the flight number you give us and adjust the pickup time automatically. Delay waiting time is free — you will not be charged for arriving late.",
  },
  {
    q: "Where does the driver meet me at Melbourne Airport?",
    a: "Your chauffeur meets you in the arrivals hall at T1, T2, T3 or T4 with a name board, or waits at the kerbside pickup zone if you prefer. We confirm the exact meeting point by SMS before you land.",
  },
  {
    q: "Can you fit 6 passengers and luggage?",
    a: "Yes. Our Premium SUV seats 6 with 5 bags, and our Maxi seats 11 with 9 bags — ideal for families and groups arriving on international flights.",
  },
  {
    q: "Do you do 3am and 4am airport pickups?",
    a: "Yes, we operate 24/7 including early-morning departures. Book the night before and your driver will arrive ahead of time.",
  },
  {
    q: "Do you charge airport parking and tolls?",
    a: "Melbourne Airport pickup fees and any tolls are added to the metered fare and shown to you before you travel, so there are no surprises on arrival.",
  },
];

const AIRPORT_REVIEWS = [
  {
    quote:
      "Booked a maxi cab for our 4am airport run from Berwick. Driver arrived early, helped with all the luggage and the fixed price was exactly as quoted.",
    name: "Daniel R.",
    date: "March 2025",
  },
  {
    quote:
      "Our flight landed 90 minutes late and the driver was still waiting at arrivals with a name board. No extra charge. Brilliant service.",
    name: "Priya S.",
    date: "February 2025",
  },
  {
    quote:
      "Used them for a Tullamarine pickup with 5 people and 6 suitcases — the SUV handled it easily and the car was spotless.",
    name: "Michael T.",
    date: "January 2025",
  },
];

export const Route = createFileRoute("/melbourne-airport-taxi")({
  head: () => ({
    meta: [
      { title: "Melbourne Airport Taxi & Transfers — Fixed Fare, 24/7" },
      {
        name: "description",
        content:
          "Fixed-fare Melbourne Airport taxi and transfers, 24/7. Flight tracking, free delay waiting, meet & greet at T1–T4. Sedan, SUV & 11-seat Maxi. Call 0493 100 265.",
      },
      { property: "og:title", content: "Melbourne Airport Taxi & Transfers — SE Premium Cabs" },
      {
        property: "og:description",
        content:
          "Fixed-fare airport transfers to and from Melbourne Airport (Tullamarine). Flight tracking, meet & greet, Sedan, SUV and Maxi. Book 24/7.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://sepremiumcabs.com/og-image.jpg" },
      { name: "twitter:image", content: "https://sepremiumcabs.com/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://sepremiumcabs.com/" },
            { "@type": "ListItem", position: 2, name: "Melbourne Airport Taxi & Transfers", item: URL },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TaxiService",
          name: "Melbourne Airport Taxi & Transfers — SE Premium Cabs",
          provider: {
            "@type": "LocalBusiness",
            name: "SE Premium Cabs",
            telephone: "+61493100265",
            email: "sepremiumcabs@gmail.com",
            url: "https://sepremiumcabs.com",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Melbourne",
              addressRegion: "VIC",
              addressCountry: "AU",
            },
          },
          areaServed: {
            "@type": "Airport",
            name: "Melbourne Airport (Tullamarine)",
            iataCode: "MEL",
          },
          availableChannel: {
            "@type": "ServiceChannel",
            serviceUrl: URL,
            servicePhone: "+61493100265",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: AIRPORT_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: AirportPage,
});

function AirportPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Toaster />
      <SiteHeader active="airport" />

      <main className="mx-auto max-w-[1400px] px-6 py-8 space-y-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">
          <section className="relative overflow-hidden rounded-xl border border-border/60 bg-card p-8 lg:p-12">
            <img
              src={heroSedan}
              alt="Premium black sedan waiting for an airport transfer at Melbourne Airport"
              width={1280}
              height={720}
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
            <div className="relative z-10">
            <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.3em] text-primary">
              <Plane className="h-4 w-4" />
              MELBOURNE AIRPORT (TULLAMARINE)
            </div>
            <h1 className="mt-4 font-serif text-4xl font-bold tracking-tight md:text-5xl">
              MELBOURNE AIRPORT TAXI &amp; TRANSFERS
              <span className="block text-primary">TRANSPARENT FARES, 24/7</span>
            </h1>
            <p className="mt-5 max-w-2xl text-muted-foreground">
              Chauffeured airport transfers to and from all Melbourne Airport terminals.
              Fares follow the official Victorian taxi rates, your flight is tracked, and
              your driver waits free of charge if you land late. Premium Sedan, Premium SUV
              and 11-seat Maxi available every hour of the day.
            </p>
            <MobileHeroCta quoteLabel="Get airport quote" />
            <div className="mt-8 hidden flex-wrap gap-4 lg:flex">
              <a
                href="#book"
                className="flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold tracking-wider text-primary-foreground transition-transform hover:scale-[1.02]"
              >
                <Car className="h-4 w-4" />
                BOOK AIRPORT TRANSFER
              </a>
              <a
                href="tel:0493100265"
                onClick={() => trackConversion("phoneCall", { airport: true })}
                className="flex items-center gap-2 rounded-md border border-primary px-6 py-3 text-sm font-bold tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
              >
                <Phone className="h-4 w-4" />
                CALL 0493 100 265
              </a>
            </div>
            <div className="mt-10 grid grid-cols-2 gap-6 sm:grid-cols-4">
              {[
                { icon: Plane, label: "Flight tracking included" },
                { icon: Clock, label: "Free waiting on delays" },
                { icon: Users, label: "Meet & greet with name board" },
                { icon: ShieldCheck, label: "CPV-accredited drivers" },
              ].map((f) => (
                <div key={f.label} className="text-center">
                  <f.icon className="mx-auto h-6 w-6 text-primary" />
                  <div className="mt-2 text-xs text-muted-foreground">{f.label}</div>
                </div>
              ))}
            </div>
            </div>
          </section>

          <div id="book" className="scroll-mt-24">
            <BookingPanel
              heading="BOOK AIRPORT TRANSFER"
              subheading="Add your flight number and we'll track your arrival"
              conversionName="airportBooking"
            />
          </div>
        </div>

        <TrustRow />

        <section className="rounded-xl border border-border/60 bg-card p-8">
          <h2 className="font-serif text-2xl font-bold tracking-tight">
            Where we meet you — Terminals T1 to T4
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { t: "T1 — Qantas", d: "Meet at the arrivals hall near the coffee kiosk, ground level." },
              { t: "T2 — International", d: "Meet just outside customs in the international arrivals hall." },
              { t: "T3 — Virgin Australia", d: "Meet at the baggage claim exit, ground level." },
              { t: "T4 — Jetstar / Rex", d: "Meet at the T4 arrivals walkway, ground level." },
            ].map((x) => (
              <div key={x.t} className="rounded-lg border border-border/60 bg-secondary/40 p-5">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-bold tracking-wide">{x.t}</h3>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{x.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-5 text-xs text-muted-foreground">
            Prefer kerbside? Tell us when booking and your driver will collect you from the
            pickup zone instead. Departing passengers are dropped directly at their terminal.
          </p>
        </section>

        <section className="rounded-xl border border-border/60 bg-card p-8">
          <h2 className="font-serif text-2xl font-bold tracking-tight">
            Melbourne Airport routes
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            We charge the official Victorian metered taxi rates — no surge, no surprises.
            Distances below are approximate; get your quote in seconds using the booking
            form above or call our 24/7 dispatch.
          </p>
          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead>
                <tr className="border-b border-border/60 text-[10px] font-bold tracking-widest text-muted-foreground">
                  <th className="py-3">MELBOURNE AIRPORT TO</th>
                  <th className="py-3">APPROX. KM</th>
                  <th className="py-3">YOUR FARE</th>
                </tr>
              </thead>
              <tbody>
                {ROUTES.map((f) => (
                  <tr key={f.to} className="border-b border-border/40">
                    <td className="py-3 font-semibold">{f.to}</td>
                    <td className="py-3 text-muted-foreground">{f.km} km</td>
                    <td className="py-3">
                      <a
                        href="#book"
                        className="text-xs font-semibold tracking-wide text-primary underline-offset-4 hover:underline"
                      >
                        Get your quote
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Tolls, airport pickup fees, traffic and your exact addresses affect the final
            price. Enter your pickup and drop-off above for a live estimate, or call us for
            a confirmed price.
          </p>
          <a
            href="tel:0493100265"
            onClick={() => trackConversion("phoneCall", { airport: true })}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
          >
            <Phone className="h-4 w-4" />
            Get your fare now
          </a>
        </section>

        <section className="rounded-xl border border-border/60 bg-card p-8">
          <h2 className="font-serif text-2xl font-bold tracking-tight">
            Vehicles for airport runs
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {[
              { name: "Premium Sedan", pax: 4, bags: 3, img: vehicleSedan, d: "Business travellers and couples with standard luggage." },
              { name: "Premium SUV", pax: 6, bags: 5, img: vehicleSuv, d: "Families and small groups with oversized suitcases." },
              { name: "Maxi (11 Seater)", pax: 11, bags: 9, img: vehicleMaxi, d: "Large groups, international arrivals and sports teams." },
            ].map((v) => (
              <div key={v.name} className="overflow-hidden rounded-lg border border-border/60 bg-secondary/40">
                <img
                  src={v.img}
                  alt={`${v.name} for Melbourne Airport transfers`}
                  width={800}
                  height={520}
                  loading="lazy"
                  decoding="async"
                  className="h-40 w-full object-cover"
                />
                <div className="p-6">
                <h3 className="text-sm font-bold tracking-wide">{v.name}</h3>
                <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" /> {v.pax} passengers
                  </span>
                  <span className="flex items-center gap-1">
                    <Luggage className="h-3 w-3" /> {v.bags} bags
                  </span>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{v.d}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-border/60 bg-card p-8">
          <h2 className="font-serif text-2xl font-bold tracking-tight">
            Airport transfer reviews
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {AIRPORT_REVIEWS.map((r) => (
              <figure key={r.name} className="rounded-lg border border-border/60 bg-secondary/40 p-6">
                <div className="flex gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <blockquote className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  "{r.quote}"
                </blockquote>
                <figcaption className="mt-3 flex items-center justify-between text-xs">
                  <span className="font-semibold tracking-wide text-primary">{r.name}</span>
                  <span className="text-muted-foreground">{r.date} · Google review</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="rounded-xl border border-border/60 bg-card p-8">
          <div className="flex items-center gap-3">
            <HelpCircle className="h-6 w-6 text-primary" />
            <h2 className="font-serif text-2xl font-bold tracking-tight">
              Melbourne Airport transfer FAQs
            </h2>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {AIRPORT_FAQS.map((f) => (
              <div key={f.q} className="rounded-lg border border-border/60 bg-secondary/40 p-5">
                <h3 className="text-sm font-semibold">{f.q}</h3>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs">
            <Link
              to="/blog/$slug"
              params={{ slug: "how-much-is-a-taxi-from-melbourne-airport-to-cbd" }}
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              How much is a taxi from Melbourne Airport to the CBD?
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "maxi-cab-melbourne-airport-groups-luggage" }}
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Maxi cab from Melbourne Airport: groups &amp; luggage guide
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "taxi-to-tullamarine-airport-timing-pickup-points" }}
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Taxi to Tullamarine: timing &amp; terminal drop-off points
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "dandenong-to-melbourne-airport-taxi" }}
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Dandenong to Melbourne Airport: route &amp; travel time
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "early-morning-airport-taxi-melbourne-3am-pickups" }}
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Early morning airport taxi: 3am &amp; 4am pickups
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "baby-seat-taxi-melbourne-child-seats" }}
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Baby seat taxi Melbourne: child seats &amp; prams
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "melbourne-airport-parking-vs-taxi-cost" }}
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Airport parking vs a taxi: which costs less?
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "station-pier-cruise-transfer-melbourne-taxi" }}
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Station Pier cruise transfers guide
            </Link>
          </div>
        </section>
      </main>

      <SiteFooter />
      <div className="h-16 lg:hidden" />
      <StickyMobileBar />
      <IdleCallNudge />
    </div>
  );
}